<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("item_return_entry") }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/sales/return/list" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="submitReturnItem" enctype="multipart/form-data">
        <!-- Header Form Section -->
        <div class="row gy-3 gx-4 mb-3 return-header-fields">
          <div class="col-md-6">
            <div class="return-field-row">
              <label class="return-field-label">{{$t('receiving_no')}}:</label>
              <div class="return-field-control">
                <input type="text" class="form-control" v-model="item.receiving_no" readonly/>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="return-field-row">
              <label class="return-field-label">{{$t('receiving_date')}}:</label>
              <div class="return-field-control">
                <input type="date" class="form-control" v-model="item.receiving_date" @change="fetchReceivingNo" />
                <div v-if="errors.receiving_date" class="text-danger mt-1 small">
                  <span>{{errors.receiving_date}}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="return-field-row return-field-row-top">
              <label class="return-field-label">{{$t('search_voucher')}}:</label>
              <div class="return-field-control position-relative">
                <input
                  ref="voucherSearch"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  v-model="q"
                  @input="searchVoucher"
                  :placeholder="$t('search_voucher_by_id')"
                  class="form-control search-input-with-clear"
                  @keydown="handleKeysVoucher"
                />
                <button
                  v-if="q || voucher_result.length"
                  type="button"
                  class="search-clear"
                  :title="$t('clear') || 'Clear'"
                  @click="clearVoucherSearch"
                >
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>

                <div
                  v-if="q"
                  class="search-results shadow rounded bg-white position-absolute mt-1"
                  style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                  :class="{ 'd-block': voucher_result.length > 0, 'd-none': voucher_result.length === 0 }"
                >
                  <ul class="list-group list-group-flush">
                    <li
                      v-for="(voucher, index) in voucher_result"
                      :key="voucher.id || voucher.voucher_id"
                      class="list-group-item d-flex justify-content-between align-items-center"
                      :class="{ 'bg-primary text-white': index === itemIndex }"
                      style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                    >
                      <div
                        class="flex-grow-1 d-flex flex-column"
                        @click="selectVoucher(voucher)"
                        style="cursor: pointer;"
                      >
                        <strong class="text-dark">{{ voucher.voucher_id }} - {{ voucher.voucher_date }}</strong>
                      </div>
                    </li>
                  </ul>
                  <div v-if="!voucher_result.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                    No results found
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="return-field-row return-field-row-top">
              <label class="return-field-label">{{$t('search_item')}}:</label>
              <div class="return-field-control position-relative">
                <input
                  ref="itemSearch"
                  type="text"
                  autocomplete="off"
                  v-model="query"
                  @input="searchItems()"
                  :placeholder="$t('search_item_by_name_barcode_serial')"
                  class="form-control search-input-with-clear"
                  @keydown="handleKeys"
                  :disabled="!selectedVoucher"
                />
                <button
                  v-if="(query || results.length) && selectedVoucher"
                  type="button"
                  class="search-clear"
                  :title="$t('clear') || 'Clear'"
                  @click="clearItemSearch"
                >
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>

                <div
                  v-if="results.length"
                  class="search-results shadow rounded bg-white position-absolute mt-1"
                  style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                >
                  <ul class="list-group list-group-flush">
                    <li
                      v-for="(item, index) in results"
                      :key="`${item.id}-${item.variant_id || 0}-${item.matched_serial_id || index}`"
                      class="list-group-item d-flex justify-content-between align-items-center"
                      :class="{ 'bg-primary text-white': index === itemIndex }"
                      style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                    >
                      <div
                        class="flex-grow-1 d-flex flex-column"
                        @click="selectItem(item)"
                        style="cursor: pointer;"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div
                            style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;"
                          >
                            <img
                              :src="$getRowImageUrl(item)"
                              alt="Items"
                              style="width:100%; height:100%; object-fit:cover;"
                            />
                          </div>

                          <div class="d-flex flex-column">
                            <strong class="text-dark">{{ formatSearchItemTitle(item) }}</strong>
                            <span style="font-size: 0.75rem; width: fit-content; color: black">
                              Invoice Qty: {{ getAvailableStock(item) }}
                            </span>
                            <span
                              v-if="item.serial_number"
                              style="font-size:12px;color:black;"
                            >
                              {{ $t('serial_number') }}: {{ item.serial_number }}
                            </span>

                            <template v-if="item.variant_id">
                              <span
                                  v-if="item.sku"
                                  style="font-size:12px;color:black;"
                              >
                                  SKU: {{ item.sku }}
                              </span>

                              <span
                                  v-if="item.barcode"
                                  style="font-size:12px;color:black;"
                              >
                                  Barcode: {{ item.barcode }}
                              </span>
                            </template>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <div v-if="!results.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                    No results found
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive gy-2 gx-4 mb-3 p-3 fixed-table">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 8.33%">{{$t('item_name')}}</th>
                <th style="width: 8.33%">{{$t('unit_price')}}</th>
                <th style="width: 8.33%">{{$t('quantity')}}</th>
                <th style="width: 8.33%">{{$t('total_discount')}}</th>
                <th style="width: 8.33%">{{$t('total_vat')}}</th>
                <th style="width: 8.33%">{{$t('total_price')}}</th>
                <th style="width: 10%">{{$t('return_qty')}}</th>
                <th style="width: 10%">{{$t('discount')}}</th>
                <th style="width: 10%">{{$t('vat')}}</th>
                <th style="width: 10%">{{$t('return_total_price')}}</th>
                <th style="width: 14%">Reason</th>
                <th style="width: 10%">{{ $t('serial_numbers') }}</th>
                <th style="width: 10%" >{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cust, index) in addedItems" :key="index" v-if="selectedItem" class="readonly-cell">
                <td class="readonly-cell">{{ cust.item_name }}</td>
                <td class="readonly-cell">{{ cust.unit_price }}</td>
                <td class="readonly-cell">{{ cust.quantity }}</td>
                <td class="readonly-cell">{{ cust.total_discount }}</td>
                <td class="readonly-cell">{{ cust.total_vat }}</td>
                <td class="readonly-cell">{{ cust.total_price }}</td>
                <td class="dynamic-cell">
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="cust.return_qty"
                    @input="validateReturnQty(cust)"
                    min="1"
                    :max="cust.quantity"
                    :readonly="cust.item_type_code === 'WARRANTY'"
                  />
                </td>
                <td class="dynamic-cell">{{ cust.discount }}</td>
                <td class="dynamic-cell">{{ cust.vat }}</td>
                <td class="dynamic-cell">{{ cust.return_total_price }}</td>
                <td class="dynamic-cell">
                  <input
                    v-model="cust.reason"
                    type="text"
                    class="form-control"
                    list="return-reasons"
                    maxlength="200"
                    placeholder="Why returned?"
                  />
                </td>
                <td class="dynamic-cell">
                  <div v-if="cust.item_type_code === 'WARRANTY' && cust.available_serials?.length">
                    <label
                      v-for="serial in cust.available_serials"
                      :key="serial.id"
                      class="d-block small mb-1"
                    >
                      <input
                        type="checkbox"
                        class="me-1"
                        :value="serial.id"
                        v-model="cust.serial_ids"
                        @change="onReturnSerialChange(cust)"
                      />
                      {{ serial.serial_number }}
                    </label>
                  </div>
                  <span v-else>-</span>
                </td>
                <td class="dynamic-cell">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeItem(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <!-- Show empty row if no data -->
              <tr v-if="!addedItems || !addedItems.length">
                <td colspan="12" class="text-center text-muted">No Items added</td>
              </tr>
            </tbody>
          </table>
          <datalist id="return-reasons">
            <option v-for="reason in returnReasons" :key="reason" :value="reason"></option>
          </datalist>
        </div>

        <!-- Payment type and Total in One Row -->
        <div class="d-flex justify-content-between align-items-start mt-4">
          <div class="d-flex gap-4">
            <!-- Payment Type -->
            <div class="d-flex flex-column">
              <label class="form-label">
                {{$t('payment_type')}} <span class="text-danger">*</span>
              </label>
              <FormDropdown
                v-model="item.cash_type"
                :options="formattedcashtypes"
                style="width: 300px;"
              />
              <div v-if="errors.cash_type" class="text-danger">{{ errors.cash_type }}</div>
            </div>

            <!-- Bank Accounts -->
            <div v-if="isBank" class="d-flex flex-column">
              <label class="form-label">
                {{$t('bank_account')}} <span class="text-danger">*</span>
              </label>
              <FormDropdown
                v-model="item.bank_account"
                :options="formattedbankaccounts"
                style="width: 300px;"
              />
              <div v-if="errors.bank_account" class="text-danger">{{ errors.bank_account }}</div>
            </div>
          </div>

          <!-- Total Amount -->
          <div class="d-flex flex-column pe-2">
            <label class="form-label ">Total amount :</label>
            <input
              type="text"
              class="form-control text-end"
              :value="totalAmount"
              disabled
              style="width: 200px; height: 38px; font-size: 1rem; font-weight: bold;"
            />
          </div>
        </div>

        <!-- Buttons Row -->
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
          <button :disabled="saving || !isValidReturn()" type="submit" class="btn btn-submit">{{ $t('submit') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { todayLocal } from '@/utils';
import { apiErrorMessage } from '@/helpers/apiError';
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';

export default {
  components: {
    Vue3Datepicker,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      item: {
        item_id: null, voucher_date: '', voucher_id: '', item: null, quantity: 0, total_price: 0, unit_price: 0,
        receiving_no: '', receiving_date: todayLocal(), cash_type: null,
        bank_account: '', return_qty: 1, discount: 0, vat: 0, return_total_price: 0, total_discount: 0, total_vat: 0,
        total_stock: 0, total_qty: 0, batch_ids: []
      },

      errors: {},
      errorMessage: "",
      saving: false,
      query: "",
      q: "",
      results: [],
      itemIndex: 0,
      selectedItem: null,
      selectedVoucher: null,
      addedItems: [],
      voucher_result: {},
      cash_types: [],
      bank_accounts: [],
      due_entry: null,
      returnReasons: [
        'Damaged / defective', 'Wrong item', 'Size or fit issue', 'Quality issue', 'Expired',
        'Customer changed mind',
      ],
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchReceivingNo();
  },

  computed: {
    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    isBank() {
      const val = this.item.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },

    totalAmount() {
      return this.addedItems.reduce((sum, item) => {
        const amount = parseFloat(item.return_total_price) || 0;
        return sum + amount;
      }, 0).toFixed(2);
    },
  },

  watch: {
    'item.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.item.receiving_no = '';
      }
    },

    'item.cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;

      if (String(id) === '1010102') {
        if (!this.item.bank_account) {   // prevent loop
          this.item.bank_account = this.bank_accounts[0].id;
        }
      } else {
        if (this.item.bank_account !== null) {  // prevent loop
          this.item.bank_account = null;
        }
      }
    },

    addedItems: {
      deep: true,
      handler(newItems) {
        newItems.forEach(item => {
          // Warranty qty is driven by selected serial checkboxes (may be 0 until picked).
          if (item.item_type_code === 'WARRANTY') {
            const selected = (item.serial_ids || []).length;
            if (item.return_qty !== selected) {
              item.return_qty = selected;
            }
            if (item.return_qty > item.quantity) {
              item.return_qty = item.quantity;
            }
            return;
          }
          if (isNaN(item.return_qty) || item.return_qty < 1) {
            item.return_qty = 1;
          }
          if (item.return_qty > item.quantity) {
            item.return_qty = item.quantity;
          }
        });
      }
    }
  },

  methods: {
    formatSearchItemTitle(item) {
      if (!item) return '';
      const code = item.item_code ? ` - ${item.item_code}` : '';
      const attrs = (item.variant_attributes || [])
        .map((v) => {
          const name = v.attribute || v.attribute_name || '';
          const value = v.value || v.attribute_value || '';
          return name && value ? `${name}: ${value}` : (value || name);
        })
        .filter(Boolean)
        .join(' | ');
      const variant = attrs ? ` (${attrs})` : '';
      return `${item.item_name || ''}${variant}${code}`;
    },

    async fetchDropdownData() {
      try {
        const [
          cash_typeRes, receive_noRes, bank_accRes,
        ] = await Promise.all([
          this.$axios.get(apiUrls.batch_cash_type.get),
          this.$axios.get(apiUrls.return_receive_number.get),
          this.$axios.get(apiUrls.batch_bank_account.get)
        ]);

        this.cash_types = cash_typeRes?.data || [];
        this.item.receiving_no = receive_noRes?.data?.receiving_no || '';
        this.bank_accounts = bank_accRes?.data || [];

        if (this.cash_types.length > 0 && !this.item.cash_type) {
          this.item.cash_type = this.cash_types[0].id;
        }

        if (this.bank_accounts.length > 0 && !this.item.bank_account) {
          this.item.bank_account = this.bank_accounts[0].id;
        }

      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    },

    async searchVoucher() {
      if (!this.q) {
        this.voucher_result = [];
        this.results = [];
        this.query = '';
        return
      }

      try {
        const response = await this.$axios.get(apiUrls.voucher_search.get, {
          params: { q: this.q }
        })
        this.voucher_result = response.data.results;
        this.itemIndex = 0;

        // render feather icons
        await this.$nextTick(() => {
          if (window.feather) feather.replace()
        })
      } catch (error) {
        this.voucher_result = [];
        this.results = [];
        this.addedItems = [];
        this.query = '';
      }
    },

    selectVoucher(voucher) {
      this.selectedVoucher = voucher;
      this.q = voucher.voucher_id;
      this.item.voucher_id = voucher.voucher_id;
      this.item.voucher_date = voucher.voucher_date;

      this.voucher_result = [];
      this.itemIndex = 0;
      this.$nextTick(() => this.$refs.itemSearch?.focus());
    },

    clearVoucherSearch() {
      this.q = '';
      this.voucher_result = [];
      this.itemIndex = 0;
      this.selectedVoucher = null;
      this.item.voucher_id = '';
      this.item.voucher_date = '';
      this.due_entry = null;
      this.clearItemSearch({ keepFocus: false });
      this.$nextTick(() => this.$refs.voucherSearch?.focus());
    },

    clearItemSearch({ keepFocus = true } = {}) {
      this.query = '';
      this.results = [];
      this.itemIndex = 0;
      if (keepFocus) {
        this.$nextTick(() => this.$refs.itemSearch?.focus());
      }
    },

    handleKeysVoucher(event) {
      if (event.key === 'ArrowDown') {
        this.itemIndex = Math.min(this.itemIndex + 1, this.voucher_result.length - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        if (this.voucher_result[this.itemIndex]) {
          this.selectVoucher(this.voucher_result[this.itemIndex]);
        }
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.clearVoucherSearch();
        event.preventDefault();
      }
    },

    async searchItems(options = {}) {
      const listAll = Boolean(options && options.listAll);
      if (!listAll && !this.query) {
        this.results = [];
        return;
      }

      if (!this.selectedVoucher) {
        this.toast.warning("Please select Invoice first.");
        return;
      }

      try {
        const response = await this.$axios.get(apiUrls.voucher_item_search.get, {
          params: {
            voucher_id: this.item.voucher_id,
            q: listAll ? '' : this.query,
          },
        });
        this.due_entry = response.data.due_entry;
        this.results = response.data.results || [];
        this.itemIndex = 0;

        await this.$nextTick(() => {
          if (window.feather) feather.replace();
        });
      } catch (error) {
        this.due_entry = null;
        this.results = [];
      }
    },

    async selectItem(item) {
      // Serial search returns a concrete variant row; otherwise open variant picker.
      if (!item.matched_serial_id && item.variant_ids && item.variant_ids.length && !item.variant_id) {
        this.loadVariants(item);
        return;
      }

      const availableQty = this.getAvailableStock(item);
      const formattedUnitPrice = Number(item.unit_price).toFixed(2);
      const formattedTotalPrice = Number(item.total_price).toFixed(2);

      this.selectedItem = item;
      this.query = this.formatSearchItemTitle(item);

      // Check if item already exists in addedItems
      const existingItem = this.addedItems.find(i => {
        // Variant item
        if (item.variant_id) {
            return i.variant_id === item.variant_id;
        }

        // Single item
        return i.item_id === item.id && !i.variant_id;
      });

      if (availableQty <= 0) {
        this.toast.warning("This item has no available quantity!");
        return;
      }

      if (existingItem) {
        // Serial scan of an already-added warranty line: just tick that serial.
        if (
          item.item_type_code === 'WARRANTY'
          && item.matched_serial_id
          && !(existingItem.serial_ids || []).includes(item.matched_serial_id)
        ) {
          const existsInList = (existingItem.available_serials || [])
            .some((s) => Number(s.id) === Number(item.matched_serial_id));
          if (existsInList) {
            existingItem.serial_ids = [...(existingItem.serial_ids || []), item.matched_serial_id];
            this.onReturnSerialChange(existingItem);
            this.results = [];
            this.itemIndex = 0;
            this.query = '';
            return;
          }
        }
        this.toast.warning("This item is already added!");
        return;
      }

      let availableSerials = [];
      let selectedSerialIds = [];
      if (item.item_type_code === 'WARRANTY') {
        try {
          const { data } = await this.$axios.get(apiUrls.serialized_units.get, {
            params: {
              voucher_id: this.item.voucher_id,
              item_id: item.id,
              variant_id: item.variant_id || undefined,
            },
          });
          availableSerials = data.results || [];
        } catch (err) {
          console.error(err);
        }
        if (!availableSerials.length) {
          this.toast.warning(
            this.$t('no_sold_serials_for_return')
            || 'No sold serial numbers left to return for this item on the selected invoice.'
          );
          return;
        }
        if (item.matched_serial_id) {
          const match = availableSerials.find(
            (s) => Number(s.id) === Number(item.matched_serial_id)
          );
          if (match) {
            selectedSerialIds = [match.id];
          }
        }
      }

      // Add selected item to the addedItems list
      const row = {
        item_id: item.id,
        variant_id: item.variant_id || null,
        reff_id: item.reff_id,
        item_name: item.item_name,
        item_code: item.item_code,
        barcode: item.barcode,
        unit_price: formattedUnitPrice,
        quantity: availableQty,
        total_price: formattedTotalPrice,
        images: item.images || [],
        receiving_no: this.item.receiving_no,
        receiving_date: this.item.receiving_date,
        cash_type: this.item.cash_type,
        bank_account: this.item.bank_account,
        discount: item.discount,
        vat: item.vat,
        base_discount: Number(item.discount),
        base_vat: Number(item.vat),
        return_qty: item.item_type_code === 'WARRANTY' ? selectedSerialIds.length : 1,
        return_quantity: item.return_qty,
        return_total_price: item.item_type_code === 'WARRANTY' && !selectedSerialIds.length
          ? '0.00'
          : formattedUnitPrice,
        total_discount: item.total_discount,
        total_vat: item.total_vat,
        batch_ids: item.batch_ids || [],
        variant_attributes: item.variant_attributes || [],
        reason: '',
        item_type_code: item.item_type_code,
        available_serials: availableSerials,
        serial_ids: selectedSerialIds,
      };
      this.addedItems.push(row);
      if (item.item_type_code === 'WARRANTY' && selectedSerialIds.length) {
        this.validateReturnQty(row);
      }

      // Clear search results and form input fields
      this.results = [];
      this.itemIndex = 0;
      this.query = "";
    },

    onReturnSerialChange(item) {
      item.return_qty = (item.serial_ids || []).length || 0;
      this.validateReturnQty(item);
    },

    async loadVariants(item) {
      try {
        const response = await this.$axios.get(
        `/return/item/${item.id}/variants/`,{
          params: {
            voucher_id: item.voucher_id,
            variant_ids: item.variant_ids
          }
        });

        const parent = item;

        this.results = response.data.results.map(v => ({
            ...v,

            // information from parent search result
            reff_id: parent.reff_id,
            voucher_id: parent.voucher_id,
            item_type_code: parent.item_type_code || v.item_type_code || null,

            // this variant's own batches / discount / vat on the sale voucher
            batch_ids: v.batch_ids || [],
            discount: v.discount,
            vat: v.vat,
            total_discount: Number(v.discount || 0) * Number(v.per_item_qty || 0),
            total_vat: Number(v.vat || 0) * Number(v.per_item_qty || 0),

            // quantity returned from backend for this variant
            per_item_qty: v.per_item_qty,

            // price actually paid per unit on the sale (not today's selling price)
            unit_price: v.unit_price,
            total_price: v.total_price,

            // keep parent item id
            id: parent.id,

            // keep parent images
            images: parent.images
        }));

        this.itemIndex = 0

      } catch (error) {
        console.error(error)
      }
    },

    getAvailableStock(item) {
      if (!item) return 0;

      // Variant
      if (item.variant_id) {
        return Number(item.per_item_qty ?? 0);
      }

      // Normal item
      return Number(
        item.total_qty ?? 0
      );
    },

    async handleKeys(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.results.length) {
          await this.searchItems({ listAll: true });
          return;
        }
        this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        const selectedProduct = this.results[this.itemIndex];
        if (selectedProduct) {
          this.selectItem(selectedProduct);
        }
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.clearItemSearch();
        event.preventDefault();
      }
    },

    async fetchReceivingNo() {
      try {
        let res = await this.$axios.get(apiUrls.return_receive_number.get, {
          params: { receiving_date: this.item.receiving_date }
        });
        this.item.receiving_no = res.data.receiving_no;
      } catch (err) {
        console.error(err);
      }
    },

    async submitReturnItem() {
      if (this.saving) return;
      if (!this.addedItems.length) {
        this.toast.warning("Please add at least one item before submitting.");
        return;
      }

      this.saving = true;
      try {
        // Prepare payload with only the required backend fields
        const payload = this.addedItems.map(item => ({
          item_id: item.item_id,
          variant_id: item.variant_id || null,
          reff_id: item.reff_id,
          item_name: item.item_name,
          item_code: item.item_code,
          barcode: item.barcode,
          unit_price: Number(item.unit_price).toFixed(2),
          quantity: item.total_qty,
          total_price: Number(item.return_total_price).toFixed(2),
          receiving_no: this.item.receiving_no,
          receiving_date: this.item.receiving_date,
          cash_type: Number(this.item.cash_type),
          bank_account: Number(this.item.bank_account),
          discount: Number(item.discount),
          vat: Number(item.vat),
          return_qty: Number(item.return_qty),
          voucher_id: this.item.voucher_id,
          due_entry: this.due_entry,
          batch_ids: item.batch_ids,
          reason: (item.reason || '').trim(),
          serial_ids: item.serial_ids || [],
        }));

        await this.$axios.post(apiUrls.return_item.post, payload);

        await this.fetchReceivingNo();
        const updatedReceivingNo = this.item.receiving_no;
        this.toast.success("Item return successfully submitted!");
        this.$router.push('/sales/return/list');
        this.$emit("create_success");

        // Reset form after save
        this.item = {
          id: null, voucher_date: '', voucher_id: '', item: null, quantity: 0, total_price: 0, unit_price: 0,
          receiving_no: updatedReceivingNo, receiving_date: this.item.receiving_date,
          cash_type: this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '', bank_account: '',
          return_total_price: 0, batch_ids: []
        }

        this.q = '';
        this.query = '';
        this.selectedItem = null;
        this.addedItems = [];

      } catch (error) {
        this.toast.error(apiErrorMessage(error));
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
      } finally {
        this.saving = false;
      }
    },

    cancelForm() {
      this.$router.push('/sales/return/list');
    },

    removeItem(index) {
      this.addedItems.splice(index, 1);
      this.query = '';
    },

    resetForm() {
      // Reset form fields and errors
      this.item = {
        id: null, voucher_date: '', voucher_id: '', item: null, quantity: 0, total_price: 0, unit_price: 0,
        cash_type: this.cash_types.length > 0 ? this.cash_types[0].id : null, receiving_no: this.item.receiving_no,
        receiving_date: this.item.receiving_date, return_total_price: 0, batch_ids: []
      };
      this.errors = {};
      this.errorMessage = "";
    },

    validateReturnQty(item) {
      if (item.item_type_code === 'WARRANTY') {
        item.return_qty = (item.serial_ids || []).length || 0;
        if (item.return_qty < 1) {
          item.discount = '0.00';
          item.vat = '0.00';
          item.return_total_price = '0.00';
          return;
        }
      } else if (isNaN(item.return_qty) || item.return_qty < 1) {
        this.toast.info("Return quantity must be at least 1.");
        return;
      }

      if (item.return_qty > item.quantity) {
        this.toast.info("Return quantity cannot exceed available quantity.");
        return;
      }

      const unit_price = Number(item.unit_price);
      item.discount = (item.base_discount * item.return_qty).toFixed(2);
      item.vat = (item.base_vat * item.return_qty).toFixed(2);
      item.return_total_price = (unit_price * item.return_qty).toFixed(2);
    },

    isValidReturn() {
      return this.addedItems.every(item => {
        if (!(item.return_qty >= 1 && item.return_qty <= item.quantity)) {
          return false;
        }
        if (item.item_type_code === 'WARRANTY') {
          const selected = item.serial_ids || [];
          return selected.length === Number(item.return_qty) && selected.length >= 1;
        }
        return true;
      });
    },
  },
};
</script>


<style scoped>
.return-field-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 38px;
}

.return-field-row-top {
  align-items: flex-start;
}

.return-field-label {
  flex: 0 0 140px;
  width: 140px;
  margin: 0;
  padding-top: 0.45rem;
  font-size: 0.875rem;
  line-height: 1.2;
  white-space: nowrap;
}

.return-field-control {
  flex: 1 1 auto;
  min-width: 0;
}

.search-input-with-clear {
  padding-right: 2.5rem;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 0.45rem;
  transform: translateY(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.65rem;
  height: 1.65rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #495057;
  color: #fff;
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
}

.search-clear:hover {
  background: #212529;
  color: #fff;
}

.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

.readonly-cell {
  background-color: #fffbe6 !important;
  font-weight: bold;
}

.dynamic-cell {
  background-color: #e6fffb !important;
}

</style>

